// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#worker-task-status
import * as React from 'react';

import { QueueCard } from '@/components/health/HealthCards';
import { LogDrawer, type LogSource } from '@/components/health/LogDrawer';
import { api } from '@/lib/api';
import { HEALTH_POLL_MS } from '@/lib/healthPoll';
import type { HealthSnapshot, HealthWorkerTaskAttention } from '@greenlight/shared';

const WORKER_TASKS_KEY = ['health-worker-tasks'] as const;

/** A kept failure log is one page: the worker capped it at 256 KiB when it wrote it. */
function failureLogSource(id: string): LogSource {
  return {
    usePage() {
      const queryKey = ['health-worker-task-failure-log', id];
      const query = api.health.workerTaskFailureLog.useQuery(
        queryKey,
        { params: { id } },
        { queryKey },
      );
      return {
        page: query.data?.status === 200 ? { text: query.data.body.text, next_cursor: null } : null,
        isLoading: query.isLoading,
        isError: query.isError,
        isFetching: query.isFetching,
        refetch: () => void query.refetch(),
      };
    },
    async readAll() {
      const result = await api.health.workerTaskFailureLog.query({ params: { id } });
      if (result.status !== 200) throw new Error(`failure log read returned ${result.status}`);
      return result.body.text;
    },
  };
}

/**
 * The Queue health card with the scheduled jobs that are failing or overdue,
 * and the drawer their latest failed run opens in. The job list is its own
 * read, so a failed read leaves the snapshot's queue sentences in place.
 */
export function QueueHealthSection({
  queue,
  trend,
}: {
  readonly queue: HealthSnapshot['queue'];
  readonly trend?: number[];
}): React.ReactElement | null {
  const query = api.health.workerTasks.useQuery(WORKER_TASKS_KEY, undefined, {
    queryKey: WORKER_TASKS_KEY,
    refetchInterval: HEALTH_POLL_MS,
  });
  const workerTasks = query.data?.status === 200 ? query.data.body.items : [];
  const [open, setOpen] = React.useState<HealthWorkerTaskAttention | null>(null);
  const logId = open?.failure_log_id ?? null;
  const source = React.useMemo(() => (logId === null ? null : failureLogSource(logId)), [logId]);

  return (
    <>
      <QueueCard
        queue={queue}
        {...(trend === undefined ? {} : { trend })}
        workerTasks={workerTasks}
        onOpenFailureLog={setOpen}
      />
      <LogDrawer
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open?.label ?? ''}
        srDescription="The last failed run of a scheduled background job"
        storageKey="internal-health-worker-task-log-width"
        testId="worker-task-failure-log"
        log={
          open !== null && source !== null && logId !== null
            ? {
                recordId: logId,
                heading: 'Last failed run',
                source,
                hasLog: true,
                emptyCopy: 'This run logged nothing.',
                caption: () =>
                  'What the job logged during this run, then its error, redacted. A long run keeps its start and its end.',
                downloadName: `background-job-failure-${logId.slice(0, 8)}.log`,
                label: 'Failed background job log',
                noun: 'log',
                testId: 'worker-task-failure-log-viewer',
              }
            : null
        }
      />
    </>
  );
}
